/**
 * Touch cart (right panel, 312px) — CRM redesign step 5.
 *
 * Reads the Selection store (ids/filter + count) for WHO, and the touchCart store
 * for the fill params (target touch + result). Chips are resolved from the current
 * volunteers page (MVP: only ids present in the visible page get a name + avatar;
 * ids off-page still count but are not chipped — the count badge stays authoritative).
 *
 * Bottom actions:
 *   - primary   → useFillTouch (cell_id is a fallback; backend uses the touch's cell).
 *   - secondary → reuse <BulkEditDialog> from BulkActions over the same selection.
 *
 * Source of truth: design_handoff_crm_redesign/README.md + mockup_2.dc.html (147-176).
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { CheckCircle2 } from "lucide-react";

import type { FilterClause, TouchSelection, Volunteer } from "@/api/client";
import { useFillTouch, useTouches, useVolunteers } from "@/api/hooks";
import { useFilters } from "@/store/filters";
import { useSelection } from "@/store/selection";
import { useTouchCart } from "@/store/touchCart";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { BulkEditDialog } from "@/components/BulkActions";
import { LotteryTurnoutBlock } from "@/components/lottery/LotteryTurnoutBlock";
import { Spinner } from "@/components/ui/spinner";
import { DateTimeInput } from "@/components/ui/datetime-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TONES } from "@/lib/tones";

/** Avatar palette cycled by chip index (mockup avBg/avFg) — семантические тона. */
const AV_CYCLE = (["blue", "green", "amber", "red", "gray"] as const).map((t) => TONES[t]);

/** "Иванов И. О." from a volunteer. */
function shortFio(v: Volunteer): string {
  const last = v.last_name?.trim() ?? "";
  const fi = v.first_name?.trim() ? `${v.first_name.trim()[0]}.` : "";
  const pat = v.patronymic?.trim() ? `${v.patronymic.trim()[0]}.` : "";
  return [last, fi, pat].filter(Boolean).join(" ") || "—";
}

/** Two-letter initials (Фамилия + Имя) for the avatar. */
function initials(v: Volunteer): string {
  const l = v.last_name?.trim()?.[0] ?? "";
  const f = v.first_name?.trim()?.[0] ?? "";
  return (l + f).toUpperCase() || "—";
}

/** Wire payload from the Selection store (ids- or filter-mode). */
function useSelectionPayload(): TouchSelection {
  const mode = useSelection((s) => s.mode);
  const ids = useSelection((s) => s.ids);
  const filters = useSelection((s) => s.filters);
  // count — информационный размер выборки; шлём его ТОЛЬКО при mode='filter'
  // (бэкенд считает по нему planned_total; сам target резолвится заново).
  const count = useSelection((s) => s.count);
  return React.useMemo<TouchSelection>(
    () =>
      mode === "filter"
        ? { mode: "filter", filters: filters ?? [], count }
        : { mode: "ids", ids: ids ?? [] },
    [mode, ids, filters, count],
  );
}

export function TouchCart() {
  const count = useSelection((s) => s.count);
  const selMode = useSelection((s) => s.mode);
  const selIds = useSelection((s) => s.ids);
  const selSampleN = useSelection((s) => s.sampleN);
  const clear = useSelection((s) => s.clear);
  const toggleId = useSelection((s) => s.toggleId);

  const touchId = useTouchCart((s) => s.touchId);
  const touchName = useTouchCart((s) => s.touchName);
  const result = useTouchCart((s) => s.result);
  const setTouch = useTouchCart((s) => s.setTouch);
  const setResult = useTouchCart((s) => s.setResult);

  const selection = useSelectionPayload();
  const fill = useFillTouch();

  const [editOpen, setEditOpen] = React.useState(false);

  // "Сейчас" vs "Запланировать" + the picked local datetime (datetime-local string).
  const [when, setWhen] = React.useState<"now" | "schedule">("now");
  const [scheduleAt, setScheduleAt] = React.useState<string>("");

  // Lower bound for the picker — текущий момент в формате datetime-local
  // ("YYYY-MM-DDTHH:mm", локальное время). Используется для min у полей.
  const nowMin = React.useMemo(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  // Validate the scheduled time: must be picked and not in the past.
  const scheduleDate = when === "schedule" && scheduleAt ? new Date(scheduleAt) : null;
  const scheduleValid =
    when === "now" ||
    (scheduleDate != null &&
      !Number.isNaN(scheduleDate.getTime()) &&
      scheduleDate.getTime() > Date.now());

  // Resolve chip names from the current volunteers page (same params as the table).
  const filters = useFilters((s) => s.filters);
  const page = useFilters((s) => s.page);
  const pageSize = useFilters((s) => s.pageSize);
  const sort = useFilters((s) => s.sort);
  const volQuery = useVolunteers({ filters, page, pageSize, sort });
  const byId = React.useMemo(() => {
    const map = new Map<number, Volunteer>();
    for (const v of volQuery.data?.results ?? []) map.set(v.id, v);
    return map;
  }, [volQuery.data]);

  // Chips only for explicit-id selections that are visible on the current page.
  const chipVolunteers = React.useMemo<Volunteer[]>(() => {
    if (selMode !== "ids" || !selIds) return [];
    return selIds.map((id) => byId.get(id)).filter((v): v is Volunteer => !!v);
  }, [selMode, selIds, byId]);

  const canFill = count > 0 && touchId != null && scheduleValid && !fill.isPending;

  // Server-side sample size comes from the store (set in ResultsHeader). Only
  // honored for filter-mode; the job samples N random ids from the full result.
  const sampleN = selMode === "filter" ? selSampleN : null;
  // Button count: N when clamped below the whole selection, otherwise the full count.
  const fillCount = sampleN != null && sampleN < count ? sampleN : count;

  // Footgun guard: server-side bulk-update has no sample_n, so editing while
  // "случайно N" is selected would silently rewrite signs for the WHOLE filter
  // result (count), not the N random people the operator believes are selected.
  // Disable bulk-edit in that mode and explain why.
  const bulkEditBlockedBySample = sampleN != null;

  function runFill() {
    if (touchId == null || count === 0 || !scheduleValid || fill.isPending) return;
    // Convert the local datetime-local value into a UTC ISO string for run_at.
    const runAt =
      when === "schedule" && scheduleDate != null ? scheduleDate.toISOString() : null;
    fill.mutate(
      {
        touchId,
        body: { cell_id: 0, result, selection, run_at: runAt, sample_n: sampleN },
      },
      {
        // Empty the cart once the job is queued so a second click can't enqueue a
        // duplicate fill for the same people/touch (F26); count→0 disables the button.
        onSuccess: () => clear(),
      },
    );
  }

  return (
    <aside
      className="flex h-full min-h-0 flex-col overflow-hidden"
      style={{
        width: 312,
        flexShrink: 0,
        background: "var(--panel, #fff)",
        borderLeft: "1px solid var(--border-hex, #E2E8F0)",
      }}
      data-testid="touch-cart"
    >
      {/* Header */}
      <div
        className="flex items-center"
        style={{
          gap: 9,
          padding: "14px 18px 12px",
          borderBottom: "1px solid var(--divider, #EBF0F6)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "var(--text-1, #334155)",
          }}
        >
          Касание-корзина
        </span>
        <span
          className="font-mono"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 22,
            height: 22,
            padding: "0 7px",
            borderRadius: 7,
            background: "var(--accent-hex, #2563EB)",
            color: "var(--on-accent, #fff)",
            fontSize: "11.5px",
            fontWeight: 700,
          }}
        >
          {count}
        </span>
        <button
          type="button"
          onClick={() => clear()}
          style={{
            marginLeft: "auto",
            fontSize: "11.5px",
            color: "var(--text-muted, #94A3B8)",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
        >
          Очистить
        </button>
      </div>

      {/* Touch picker + result radios */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--divider, #EBF0F6)", flexShrink: 0, overflowX: "hidden", minWidth: 0 }}>
        <div style={{ fontSize: "11px", color: "var(--text-muted, #94A3B8)", marginBottom: 6 }}>
          Касание
        </div>
        <TouchCombobox
          touchId={touchId}
          touchName={touchName}
          onPick={(id, name) => setTouch(id, name)}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 11,
            fontSize: "12.5px",
            color: "var(--text-2, #475569)",
          }}
        >
          <span style={{ color: "var(--text-muted, #94A3B8)" }}>Результат:</span>
          <div
            role="radiogroup"
            aria-label="Результат"
            style={{
              display: "inline-flex",
              gap: 6,
              padding: 2,
              borderRadius: 9,
              background: "var(--panel-subtle, #F7F9FC)",
              border: "1px solid var(--border-hex, #E2E8F0)",
            }}
          >
            <ResultRadio label="Да" active={result === "Да"} onSelect={() => setResult("Да")} />
            <ResultRadio label="Нет" active={result === "Нет"} onSelect={() => setResult("Нет")} />
          </div>
        </div>
      </div>

      {/* Chips — единственный прокручиваемый блок (min-h-0, чтобы корректно сжимался). */}
      <div
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
        style={{ padding: "10px 14px", minWidth: 0 }}
      >
        <div
          style={{
            fontSize: "11px",
            color: "var(--text-muted, #94A3B8)",
            padding: "0 4px 8px",
          }}
        >
          В корзине · случайную выборку задайте в шапке результатов
        </div>

        {selMode === "filter" ? (
          <div style={{ padding: "8px" }}>
            <div
              style={{
                fontSize: "12.5px",
                color: "var(--text-3, #64748B)",
              }}
            >
              {sampleN != null
                ? `Случайно ${sampleN.toLocaleString("ru-RU")} из выборки (${count.toLocaleString("ru-RU")}).`
                : `Выбрана вся выборка по фильтру (${count.toLocaleString("ru-RU")}).`}
            </div>
          </div>
        ) : (
          chipVolunteers.map((v, i) => {
            const av = AV_CYCLE[i % AV_CYCLE.length];
            return (
              <div
                key={v.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "7px 8px",
                  borderRadius: 9,
                  background: "var(--panel-subtle, #F7F9FC)",
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: av.bg,
                    color: av.fg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10.5px",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {initials(v)}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: "12.5px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {shortFio(v)}
                </span>
                <button
                  type="button"
                  aria-label="Убрать из корзины"
                  onClick={() => toggleId(v.id)}
                  style={{
                    color: "var(--control-border-2, #B6C2D2)",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Bottom actions — всегда видимы снизу (не уезжают, не скроллятся). */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid var(--divider, #EBF0F6)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flexShrink: 0,
          overflowX: "hidden",
          minWidth: 0,
        }}
      >
        {/* Когда: Сейчас / Запланировать — компактно, в одну строку. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "6px 14px",
            fontSize: "12.5px",
            color: "var(--text-2, #475569)",
            minWidth: 0,
          }}
        >
          <span style={{ color: "var(--text-muted, #94A3B8)", flexShrink: 0 }}>Когда:</span>
          <RadioGroup
            value={when}
            onValueChange={(v) => setWhen(v as "now" | "schedule")}
            disabled={fill.isPending}
            className="grid-flow-col items-center justify-start gap-4"
          >
            <label className="flex cursor-pointer items-center gap-1.5 text-sm">
              <RadioGroupItem value="now" /> Сейчас
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-sm">
              <RadioGroupItem value="schedule" /> Запланировать
            </label>
          </RadioGroup>
        </div>

        {when === "schedule" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <DateTimeInput
              id="touch-cart-schedule-at"
              value={scheduleAt}
              onChange={setScheduleAt}
              disabled={fill.isPending}
              min={nowMin}
            />
            {scheduleAt !== "" && !scheduleValid ? (
              <p className="text-xs" style={{ color: "var(--danger, #A93A3A)" }}>
                Время запуска должно быть в будущем.
              </p>
            ) : null}
          </div>
        ) : null}

        {fill.isSuccess ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "12px",
              color: "var(--success, #11774E)",
              minWidth: 0,
            }}
          >
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Наполнение запущено.
            </span>
          </div>
        ) : null}
        {fill.isError ? (
          <div
            style={{
              fontSize: "11.5px",
              color: "var(--danger, #A93A3A)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            Не удалось запустить наполнение
            {fill.error instanceof Error ? `: ${fill.error.message}` : "."}
          </div>
        ) : null}

        <button
          type="button"
          disabled={!canFill}
          onClick={runFill}
          data-testid="touch-cart-fill"
          style={{
            width: "100%",
            background: "var(--accent-hex, #2563EB)",
            color: "var(--on-accent, #fff)",
            fontSize: "13.5px",
            fontWeight: 600,
            padding: "11px",
            borderRadius: 10,
            border: "none",
            cursor: canFill ? "pointer" : "not-allowed",
            opacity: canFill ? 1 : 0.5,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {fill.isPending ? <Spinner /> : null}
          {when === "schedule"
            ? `Запланировать ${fillCount.toLocaleString("ru-RU")} чел. →`
            : `Добавить ${fillCount.toLocaleString("ru-RU")} чел. в касание →`}
        </button>
        <button
          type="button"
          disabled={count === 0 || bulkEditBlockedBySample}
          onClick={() => setEditOpen(true)}
          data-testid="touch-cart-bulk-edit"
          title={
            bulkEditBlockedBySample
              ? "Недоступно при выборе «случайно N»: правка применится ко всей выборке, а не к N случайным."
              : undefined
          }
          style={{
            width: "100%",
            background: "var(--panel, #fff)",
            border: "1px solid var(--border-hex, #E2E8F0)",
            color: "var(--text-2, #475569)",
            fontSize: "12.5px",
            padding: "9px",
            borderRadius: 10,
            cursor: count === 0 || bulkEditBlockedBySample ? "not-allowed" : "pointer",
            opacity: count === 0 || bulkEditBlockedBySample ? 0.5 : 1,
          }}
        >
          Изменить признаки выбранных
        </button>
        {bulkEditBlockedBySample ? (
          <p
            className="text-xs"
            style={{ color: "var(--text-muted, #94A3B8)", marginTop: -2, lineHeight: 1.35 }}
          >
            Недоступно при выборе «случайно N»: правка признаков применится ко всей выборке (
            {count.toLocaleString("ru-RU")}), а не к N случайным.
          </p>
        ) : null}
        <LotteryTurnoutBlock />
      </div>

      {editOpen && <BulkEditDialog open={editOpen} onOpenChange={setEditOpen} />}
    </aside>
  );
}

/**
 * Result segment toggle. Active segment is unambiguous: accent-weak fill (#EFF4FE),
 * accent border, dark-blue text (#1E40AF) plus a filled accent dot. Inactive is a
 * plain transparent segment with a muted outline dot — so "Да"/"Нет" reads at a glance.
 */
function ResultRadio({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 11px",
        borderRadius: 7,
        cursor: "pointer",
        fontSize: "12.5px",
        fontWeight: active ? 600 : 500,
        background: active ? "var(--accent-weak, #EFF4FE)" : "transparent",
        border: active ? "1px solid var(--accent-hex, #2563EB)" : "1px solid transparent",
        color: active ? "var(--accent-weak-text, #1E40AF)" : "var(--text-muted, #94A3B8)",
        transition: "background .12s, color .12s, border-color .12s",
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          flexShrink: 0,
          border: active
            ? "1.5px solid var(--accent-hex, #2563EB)"
            : "1.5px solid var(--control-border, #CBD5E1)",
          background: active ? "var(--accent-hex, #2563EB)" : "transparent",
          boxShadow: active ? "inset 0 0 0 2.5px var(--panel, #fff)" : undefined,
        }}
      />
      {label}
    </button>
  );
}

/**
 * Searchable touch picker. With 3000+ touches a flat <select> is unusable, so the
 * caller types into a text input and gets a debounced, server-side search:
 *   - numeric query  → filter by exact id  ({field:"id", operator:"among"}).
 *   - text query     → filter by name      ({field:"name", operator:"contains"}).
 * Results render in a fixed-position popover portalled into document.body — the cart's
 * ancestors use overflow-hidden (window-scroll guard), which would otherwise clip an
 * absolute popover. Picking a row lifts {id, name} up via onPick so the cart can label
 * the selection «№{id} · {name}» even though the chosen touch may be off the first
 * results page.
 */
function TouchCombobox({
  touchId,
  touchName,
  onPick,
}: {
  touchId: number | null;
  touchName: string | null;
  onPick: (id: number | null, name: string | null) => void;
}) {
  const [open, setOpen] = React.useState(touchId == null);
  const [query, setQuery] = React.useState("");
  const debounced = useDebouncedValue(query.trim(), 300);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  // Fixed-position coords for the portalled popover, measured from the input rect.
  const [pos, setPos] = React.useState<{ left: number; top: number; width: number } | null>(null);

  // Build the filter exactly like TouchesList: digits → id__among, else name contains.
  const filters = React.useMemo<FilterClause[]>(() => {
    if (debounced === "") return [];
    if (/^\d+$/.test(debounced))
      return [{ field: "id", operator: "among", value: [Number(debounced)] }];
    return [{ field: "name", operator: "contains", value: debounced }];
  }, [debounced]);

  const enabled = debounced.length > 0;
  const search = useTouches({ filters, page: 1, pageSize: 50 }, enabled);
  const results = enabled ? (search.data?.results ?? []) : [];

  const showPopover = open && enabled;

  // Recompute the popover position from the input rect.
  const updatePos = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ left: rect.left, top: rect.bottom + 4, width: rect.width });
  }, []);

  // Position on open + whenever query/results change layout; keep in sync on
  // window resize and scroll (capture — the cart's middle block scrolls, not window).
  React.useLayoutEffect(() => {
    if (!showPopover) return;
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [showPopover, debounced, search.data, updatePos]);

  // Close on outside click — must exclude BOTH the widget and the portalled popover,
  // since the popover lives outside wrapRef in the DOM.
  React.useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function pick(id: number, name: string) {
    onPick(id, name);
    setQuery("");
    setOpen(false);
  }

  // Collapsed state: show the picked touch as a chip with a "change" affordance.
  if (touchId != null && !open) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--panel-subtle, #F7F9FC)",
          border: "1px solid var(--border-hex, #E2E8F0)",
          borderRadius: 9,
          padding: "9px 12px",
          fontSize: "12.5px",
          fontWeight: 500,
        }}
        data-testid="touch-cart-selected"
      >
        <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 6, overflow: "hidden" }}>
          <span className="font-mono" style={{ color: "var(--text-muted, #94A3B8)", flexShrink: 0 }}>
            №{touchId}
          </span>
          <span
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            · {touchName ?? "—"}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            flexShrink: 0,
            fontSize: "11.5px",
            color: "var(--accent-hex, #2563EB)",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
        >
          сменить
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        autoFocus={touchId != null}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Поиск по ID или названию…"
        data-testid="touch-cart-search"
        style={{
          width: "100%",
          background: "var(--panel-subtle, #F7F9FC)",
          border: "1px solid var(--border-hex, #E2E8F0)",
          borderRadius: 9,
          padding: "9px 12px",
          fontSize: "12.5px",
          fontWeight: 500,
          outline: "none",
        }}
      />

      {showPopover && pos
        ? createPortal(
            <div
              ref={popoverRef}
              style={{
                position: "fixed",
                left: pos.left,
                top: pos.top,
                width: pos.width,
                zIndex: 50,
                maxHeight: 240,
                overflowY: "auto",
                background: "var(--panel, #fff)",
                border: "1px solid var(--border-hex, #E2E8F0)",
                borderRadius: 9,
                boxShadow: "0 8px 24px rgba(15,23,42,.12)",
                padding: 4,
              }}
              data-testid="touch-cart-results"
            >
              {search.isPending ? (
            <div style={{ padding: "8px 10px", fontSize: "12px", color: "var(--text-muted, #94A3B8)", display: "flex", alignItems: "center", gap: 8 }}>
              <Spinner /> Поиск…
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: "8px 10px", fontSize: "12px", color: "var(--text-muted, #94A3B8)" }}>
              Ничего не найдено
            </div>
          ) : (
            results.map((t) => {
              const isActive = t.id === touchId;
              return (
                <button
                  key={t.id}
                  type="button"
                  // onMouseDown (not onClick): fires before the input blur /
                  // document mousedown outside-click, so the pick lands reliably.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(t.id, t.name);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 6,
                    width: "100%",
                    textAlign: "left",
                    padding: "7px 8px",
                    borderRadius: 7,
                    border: "none",
                    cursor: "pointer",
                    background: isActive ? "var(--accent-weak, #EFF4FE)" : "transparent",
                    fontSize: "12.5px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = "var(--panel-subtle, #F7F9FC)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span className="font-mono" style={{ color: "var(--text-muted, #94A3B8)", flexShrink: 0 }}>
                    №{t.id}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    · {t.name}
                  </span>
                </button>
              );
            })
          )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
